const binCache = new Map<string, Float32Array>();

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');

/** Remove cache-busting query params from an alignment asset URL. */
export function stripAssetUrlQuery(url: string): string {
  return url.split('?')[0];
}

/** True when the URL targets a protected alignment scan API route. */
export function isProtectedAlignmentAssetUrl(url: string): boolean {
  const base = stripAssetUrlQuery(url);
  return base.includes('/alignment/sensors/') && base.includes('/scan.');
}

/** True when the URL targets a protected point cloud API route (alignment or simulate). */
export function isProtectedPointCloudAssetUrl(url: string): boolean {
  const base = stripAssetUrlQuery(url);
  if (base.includes('/alignment/sensors/') && base.includes('/scan.')) {
    return true;
  }
  return base.includes('/simulate/point-cloud/clouds/') && base.endsWith('.bin');
}

/** Resolve static or API asset paths against the API host. */
export function resolveAssetUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${API_BASE_URL}/${url.replace(/^\//, '')}`;
}

function authHeadersForAssetFetch(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Drop non-finite XYZ triplets that break Three.js bounding-sphere math. */
export function sanitizeBinPoints(points: Float32Array): Float32Array {
  const valid: number[] = [];
  for (let i = 0; i + 2 < points.length; i += 3) {
    const x = points[i];
    const y = points[i + 1];
    const z = points[i + 2];
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
      valid.push(x, y, z);
    }
  }
  return new Float32Array(valid);
}

/**
 * Fetch a sensor alignment .bin file (float32 XYZ triplets) and return cached points.
 * Protected alignment API routes require the user's bearer token.
 */
export async function loadBinPointCloud(url: string): Promise<Float32Array> {
  const resolvedUrl = resolveAssetUrl(url);
  const cached = binCache.get(resolvedUrl);
  if (cached) {
    return cached;
  }

  const fetchOptions: RequestInit = {};
  if (isProtectedPointCloudAssetUrl(url)) {
    fetchOptions.headers = authHeadersForAssetFetch();
    fetchOptions.credentials = 'include';
  }
  if (isProtectedAlignmentAssetUrl(url)) {
    fetchOptions.cache = 'no-store';
  }

  const response = await fetch(resolvedUrl, fetchOptions);
  if (!response.ok) {
    throw new Error(`Failed to load point cloud (${response.status})`);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/html')) {
    throw new Error('Received HTML instead of point cloud data');
  }

  const buffer = await response.arrayBuffer();
  if (buffer.byteLength < 12) {
    throw new Error('Point cloud data is empty');
  }
  if (buffer.byteLength % 12 !== 0) {
    throw new Error('Invalid point cloud data: byte length is not a multiple of 12');
  }

  const raw = new Float32Array(buffer);
  const points = sanitizeBinPoints(raw);
  if (points.length === 0) {
    throw new Error('Point cloud contains no valid points');
  }

  binCache.set(resolvedUrl, points);
  return points;
}

/** Clear cached bin data (useful in tests). */
export function clearBinPointCloudCache(): void {
  binCache.clear();
}

/** Drop cached entries for one URL or every variant sharing the same base path. */
export function invalidateBinPointCloudCache(url?: string): void {
  if (!url) {
    binCache.clear();
    return;
  }

  const resolved = resolveAssetUrl(url);
  const base = stripAssetUrlQuery(resolved);
  for (const key of Array.from(binCache.keys())) {
    if (key === resolved || stripAssetUrlQuery(key) === base) {
      binCache.delete(key);
    }
  }
}
